/**
 * SpacetimeDB-Verbindung (W3, docs/09 §6): Builder + Token-Persistenz +
 * Auto-Reconnect mit Backoff. Token in localStorage — beide Tabs derselben
 * Origin teilen die Identity (2-Tab-DoD).
 *
 * BATTLE-23-Härtung: scheduleRetry ist idempotent (das SDK feuert bei einem
 * Socket-Fehler onConnectError UND onDisconnect — ohne Guard entstehen
 * parallele Retry-Ketten). Known-Limitation: Öffnen zwei Tabs GLEICHZEITIG
 * ohne gespeichertes Token, erzeugen beide eine eigene Identity (letzte
 * gewinnt localStorage; heilt beim nächsten Reload) — echter Fix braucht
 * navigator.locks und kommt mit Stufe-4-Auth.
 */
import { DbConnection } from './module_bindings'
import type { Identity } from 'spacetimedb'

const TOKEN_KEY = 'autobattler.stdb.token'

export interface StdbStatus {
  online: boolean
  /** menschlicher Kurztext fuer das Verbindungs-Banner */
  detail: string
}

export interface StdbSession {
  conn: DbConnection
  identity: Identity
}

export interface StdbConnectOptions {
  uri: string
  dbName: string
  onStatus(status: StdbStatus): void
  /** wird bei JEDER (Re-)Verbindung gerufen — Subscriptions hier neu aufsetzen */
  onSession(session: StdbSession): void
}

/** Erstverbindung; loest erst auf, wenn die erste Verbindung steht (oder scheitert). */
export function connectStdb(opts: StdbConnectOptions): Promise<StdbSession> {
  let attempts = 0
  let settled = false
  let retryPending = false

  return new Promise<StdbSession>((resolve, reject) => {
    const build = (): void => {
      opts.onStatus({ online: false, detail: attempts === 0 ? 'Verbinde…' : `Neuverbindung (Versuch ${attempts + 1})…` })
      const stored = localStorage.getItem(TOKEN_KEY)
      let builder = DbConnection.builder()
        .withUri(opts.uri)
        .withDatabaseName(opts.dbName)
        .onConnect((conn: DbConnection, identity: Identity, token: string) => {
          attempts = 0
          localStorage.setItem(TOKEN_KEY, token)
          opts.onStatus({ online: true, detail: 'Verbunden' })
          const session = { conn, identity }
          opts.onSession(session)
          if (!settled) {
            settled = true
            resolve(session)
          }
        })
        .onDisconnect(() => {
          opts.onStatus({ online: false, detail: 'Verbindung getrennt — verbinde neu…' })
          scheduleRetry()
        })
        .onConnectError((_ctx: unknown, err: Error) => {
          // Ungueltiges (z.B. von einer anderen Instanz signiertes) Token verwerfen
          if (stored && /token|unauthorized|401/i.test(String(err))) {
            localStorage.removeItem(TOKEN_KEY)
          }
          if (!settled && attempts >= 2) {
            settled = true
            reject(err instanceof Error ? err : new Error(String(err)))
            return
          }
          scheduleRetry()
        })
      if (stored) builder = builder.withToken(stored)
      builder.build()
    }

    // Idempotent: onConnectError + onDisconnect desselben Sockets ergeben EINEN Retry
    const scheduleRetry = (): void => {
      if (retryPending) return
      retryPending = true
      attempts++
      const delay = Math.min(15000, 500 * 2 ** Math.min(attempts, 5))
      setTimeout(() => {
        retryPending = false
        build()
      }, delay)
    }

    build()
  })
}
